import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { GraduationCap, DollarSign, Check, X, Loader2, Copy, BadgeCheck, Wallet, FileText, Zap, CalendarClock, History, ChevronDown, ChevronRight } from "lucide-react";

export function MentorsTab() {
  const [apps, setApps] = useState([]);
  const [rates, setRates] = useState({ referral_pct: 5, mentor_pct: 10 });
  const [ledger, setLedger] = useState({ rows: [], by_person: [], totals: { pending: 0, paid: 0, count: 0 } });
  const [busy, setBusy] = useState(null);
  const [creds, setCreds] = useState(null);
  const [selected, setSelected] = useState([]);
  const [schedule, setSchedule] = useState({ friday_auto: false, last_run: null });
  const [history, setHistory] = useState({ batches: [], grand_total: 0, batch_count: 0 });
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    const [a, r, l, s, h] = await Promise.allSettled([
      api.get("/admin/mentor-applications"),
      api.get("/admin/commission-config"),
      api.get("/admin/commissions"),
      api.get("/admin/payout-schedule"),
      api.get("/admin/commissions/payout-history"),
    ]);
    if (a.status === "fulfilled") setApps(a.value.data || []);
    if (r.status === "fulfilled") setRates(r.value.data);
    if (l.status === "fulfilled") setLedger(l.value.data);
    if (s.status === "fulfilled") setSchedule(s.value.data);
    if (h.status === "fulfilled") setHistory(h.value.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveRates = async () => {
    setBusy("rates");
    try {
      const { data } = await api.post("/admin/commission-config", {
        referral_pct: Number(rates.referral_pct), mentor_pct: Number(rates.mentor_pct) });
      setRates(data); toast.success("Pousantaj komisyon anrejistre");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const approve = async (id) => {
    setBusy("app-" + id);
    try {
      const { data } = await api.post(`/admin/mentor-applications/${id}/approve`, {});
      setCreds(data); toast.success(`Mentor apwouve: ${data.email}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const reject = async (id) => {
    setBusy("rej-" + id);
    try { await api.post(`/admin/mentor-applications/${id}/reject`); toast.success("Aplikasyon rejte"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const markPaid = async (cid) => {
    setBusy("paid-" + cid);
    try { await api.post(`/admin/commissions/${cid}/mark-paid`); toast.success("Make peye"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const payAll = async (bid) => {
    setBusy("payall-" + bid);
    try { const { data } = await api.post(`/admin/commissions/pay-all/${bid}`); toast.success(`${data.paid} komisyon make peye`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const payableList = ledger.by_person.filter((b) => (b.pending || 0) > 0);
  const toggleSelect = (bid) =>
    setSelected((s) => (s.includes(bid) ? s.filter((x) => x !== bid) : [...s, bid]));
  const allSelected = payableList.length > 0 && selected.length === payableList.length;
  const toggleSelectAll = () =>
    setSelected(allSelected ? [] : payableList.map((b) => b.beneficiary_id));
  const selectedTotal = payableList
    .filter((b) => selected.includes(b.beneficiary_id))
    .reduce((sum, b) => sum + (b.pending || 0), 0);

  const paySelected = async () => {
    if (selected.length === 0) { toast.error("Chwazi omwen yon benefisyè"); return; }
    setBusy("pay-selected");
    try {
      const { data } = await api.post("/admin/commissions/pay-selected", { beneficiary_ids: selected });
      const skip = (data.skipped || []).length;
      toast.success(`${data.paid} komisyon transfere ($${data.total})${skip ? ` · ${skip} sote (poko pare)` : ""}`);
      setSelected([]); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const payAllGlobal = async () => {
    if (!window.confirm("Ou sèten ou vle peye TOUT komisyon annatant yo?")) return;
    setBusy("pay-global");
    try {
      const { data } = await api.post("/admin/commissions/pay-all-now");
      const skip = (data.skipped || []).length;
      toast.success(`${data.paid} komisyon transfere ($${data.total})${skip ? ` · ${skip} sote (poko pare)` : ""}`);
      setSelected([]); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const toggleFriday = async (val) => {
    setSchedule((s) => ({ ...s, friday_auto: val }));
    try {
      await api.post("/admin/payout-schedule", { friday_auto: val });
      toast.success(val ? "Peman otomatik chak Vandredi AKTIVE" : "Peman otomatik Vandredi DEZAKTIVE");
    } catch (e) {
      setSchedule((s) => ({ ...s, friday_auto: !val }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pending = apps.filter((a) => a.status === "pending");
  const [remindingStripe, setRemindingStripe] = useState(false);

  return (
    <div className="space-y-6" data-testid="mentors-tab">
      {/* Remind mentors to connect Stripe */}
      <div className="border border-[#222] bg-[#0c0c0c] rounded-lg p-4 flex items-center gap-3" data-testid="remind-mentors-stripe-card">
        <Wallet className="h-5 w-5 text-[#00F593] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white">Raple mentor yo konekte Stripe</div>
          <div className="text-[11px] text-[#888]">Voye yon imèl + notifikasyon bay chak mentor ki poko konekte kont Stripe yo pou peman.</div>
        </div>
        <Button data-testid="remind-mentors-stripe-button" disabled={remindingStripe}
          onClick={async () => {
            setRemindingStripe(true);
            try {
              const { data } = await api.post("/admin/remind-mentors-stripe");
              toast.success(`Raple voye bay ${data.reminded} mentor`);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            finally { setRemindingStripe(false); }
          }}
          className="bg-[#00F593] hover:bg-[#00d97f] text-black font-semibold rounded-lg h-9 shrink-0">
          {remindingStripe ? <Loader2 className="h-4 w-4 animate-spin" /> : "Voye Raple"}
        </Button>
      </div>

      {/* Commission rates */}
      <div className="border border-[#222] bg-[#0F0F0F] p-5 max-w-2xl">
        <div className="flex items-center gap-2 mb-4"><DollarSign className="h-4 w-4 text-[#00F593]" />
          <span className="font-heading font-semibold">Pousantaj Komisyon</span></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Referral Kliyan (%)</label>
            <Input type="number" value={rates.referral_pct} data-testid="referral-pct-input"
              onChange={(e) => setRates({ ...rates, referral_pct: e.target.value })}
              className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
          </div>
          <div>
            <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Mentor (%)</label>
            <Input type="number" value={rates.mentor_pct} data-testid="mentor-pct-input"
              onChange={(e) => setRates({ ...rates, mentor_pct: e.target.value })}
              className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
          </div>
        </div>
        <Button onClick={saveRates} disabled={busy === "rates"} data-testid="save-rates-button"
          className="mt-4 h-10 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold px-6">
          {busy === "rates" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anrejistre"}
        </Button>
      </div>

      {/* Generated mentor credentials */}
      {creds && (
        <div className="border border-[#00F593]/40 bg-[#00F593]/5 p-5 max-w-2xl" data-testid="mentor-creds">
          <div className="flex items-center gap-2 mb-2"><BadgeCheck className="h-4 w-4 text-[#00F593]" />
            <span className="font-heading font-semibold text-[#00F593]">Mentor Kreye — bay li enfòmasyon sa yo</span></div>
          <div className="font-mono-term text-sm space-y-1">
            <div>Email: <b className="text-white">{creds.email}</b></div>
            <div className="flex items-center gap-2">Modpas tanporè: <b className="text-white">{creds.temp_password}</b>
              <button onClick={async () => { try { await navigator.clipboard.writeText(creds.temp_password); toast.success("Kopye"); } catch { toast.error("Kopye manyèlman"); } }}><Copy className="h-3.5 w-3.5 text-[#8fb4e6]" /></button>
            </div>
            <div>Komisyon: <b className="text-white">{creds.commission_pct}%</b></div>
          </div>
          <p className="text-[11px] text-[#8fb4e6] mt-2">Mentor la ka chanje modpas li apre premye koneksyon.</p>
        </div>
      )}

      {/* Applications */}
      <div>
        <div className="flex items-center gap-2 mb-3"><GraduationCap className="h-4 w-4 text-[#1C8CFF]" />
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Aplikasyon Mentor ({pending.length} annatant)</span></div>
        <div className="border border-[#222] bg-[#0F0F0F]" data-testid="mentor-applications">
          {apps.length === 0 ? <div className="p-8 text-center text-[#888] text-sm">Pa gen aplikasyon.</div> :
            apps.map((a) => (
              <div key={a.id} data-testid={`mentor-app-${a.id}`} className="px-4 py-3 border-b border-[#161616] last:border-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-sm">{a.name} <span className={`ml-1.5 text-[10px] font-mono-term uppercase px-1.5 py-0.5 rounded-sm ${a.status === "pending" ? "bg-[#f5a623]/10 text-[#f5a623]" : a.status === "approved" ? "bg-[#00F593]/10 text-[#00F593]" : "bg-[#FF3B30]/10 text-[#FF3B30]"}`}>{a.status}</span></div>
                    <div className="font-mono-term text-xs text-[#888]">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
                    {a.experience && <div className="text-xs text-[#8fb4e6] mt-1">Eksperyans: {a.experience}</div>}
                    {a.message && <p className="text-xs text-[#999] mt-1">{a.message}</p>}
                    {a.payout_email && <div className="font-mono-term text-[11px] text-[#666] mt-1">Imèl pou peye: {a.payout_email}</div>}
                    {(a.id_front_url || a.id_back_url || a.account_proof_url) && (
                      <div className="flex flex-wrap gap-2 mt-2" data-testid={`mentor-app-docs-${a.id}`}>
                        {a.id_front_url && <a href={a.id_front_url} target="_blank" rel="noreferrer" data-testid={`mentor-doc-front-${a.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-[#1C8CFF]/10 text-[#1C8CFF] hover:bg-[#1C8CFF]/20"><FileText className="h-3 w-3" /> ID Fas</a>}
                        {a.id_back_url && <a href={a.id_back_url} target="_blank" rel="noreferrer" data-testid={`mentor-doc-back-${a.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-[#1C8CFF]/10 text-[#1C8CFF] hover:bg-[#1C8CFF]/20"><FileText className="h-3 w-3" /> ID Do</a>}
                        {a.account_proof_url && <a href={a.account_proof_url} target="_blank" rel="noreferrer" data-testid={`mentor-doc-account-${a.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-[#00F593]/10 text-[#00F593] hover:bg-[#00F593]/20"><FileText className="h-3 w-3" /> Kont Reyèl</a>}
                      </div>
                    )}
                  </div>
                  {a.status === "pending" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" onClick={() => approve(a.id)} disabled={busy === "app-" + a.id} data-testid={`approve-mentor-${a.id}`}
                        className="h-9 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold"><Check className="mr-1 h-4 w-4" /> Apwouve</Button>
                      <Button size="sm" variant="ghost" onClick={() => reject(a.id)} disabled={busy === "rej-" + a.id} data-testid={`reject-mentor-${a.id}`}
                        className="h-9 rounded-sm text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"><X className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Commission ledger */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#888] flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Ledger Komisyon</span>
          <span className="font-mono-term text-[11px]"><span className="text-[#f5a623]">Pending ${ledger.totals.pending}</span> · <span className="text-[#00F593]">Paid ${ledger.totals.paid}</span></span>
        </div>

        {/* CEO payout actions: select & pay, pay all, Friday auto */}
        <div className="border border-[#222] bg-[#0F0F0F] p-4 mb-3 space-y-3" data-testid="payout-actions">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={toggleSelectAll} disabled={payableList.length === 0}
              variant="ghost" data-testid="select-all-payout-button"
              className="h-9 rounded-sm text-xs text-[#8fb4e6] hover:bg-[#1C8CFF]/10 hover:text-[#1C8CFF]">
              {allSelected ? "Retire tout" : "Chwazi tout"}
            </Button>
            <Button size="sm" onClick={paySelected} disabled={selected.length === 0 || busy === "pay-selected"}
              data-testid="pay-selected-button"
              className="h-9 rounded-sm bg-[#1C8CFF] text-white hover:bg-[#1678e0] text-xs font-semibold px-4">
              {busy === "pay-selected" ? <Loader2 className="h-4 w-4 animate-spin" /> :
                <><Check className="mr-1 h-4 w-4" /> Peye seleksyone yo{selected.length ? ` (${selected.length} · $${selectedTotal.toFixed(2)})` : ""}</>}
            </Button>
            <Button size="sm" onClick={payAllGlobal} disabled={ledger.totals.pending <= 0 || busy === "pay-global"}
              data-testid="pay-all-global-button"
              className="h-9 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] text-xs font-semibold px-4">
              {busy === "pay-global" ? <Loader2 className="h-4 w-4 animate-spin" /> :
                <><Zap className="mr-1 h-4 w-4" /> Peye TOUT (${ledger.totals.pending})</>}
            </Button>
          </div>
          <div className="flex items-center justify-between border-t border-[#161616] pt-3" data-testid="friday-auto-row">
            <div className="flex items-center gap-2 min-w-0">
              <CalendarClock className="h-4 w-4 text-[#f5a623] shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-heading font-semibold">Otomatikman peye chak Vandredi</div>
                <div className="font-mono-term text-[11px] text-[#666]">
                  {schedule.last_run ? `Dènye peman otomatik: ${new Date(schedule.last_run).toLocaleString()}` : "Sistèm nan ap peye tout komisyon annatant chak Vandredi (UTC)."}
                </div>
              </div>
            </div>
            <Switch checked={!!schedule.friday_auto} onCheckedChange={toggleFriday} data-testid="friday-auto-toggle" />
          </div>

          {/* Payout history */}
          <div className="border-t border-[#161616] pt-3" data-testid="payout-history">
            <button onClick={() => setShowHistory((v) => !v)} data-testid="payout-history-toggle"
              className="w-full flex items-center justify-between text-left group">
              <span className="flex items-center gap-2 text-sm font-heading font-semibold">
                <History className="h-4 w-4 text-[#1C8CFF]" /> Istorik Peman
                <span className="font-mono-term text-[10px] uppercase text-[#666] ml-1">{history.batch_count} batch · ${history.grand_total} total</span>
              </span>
              {showHistory ? <ChevronDown className="h-4 w-4 text-[#888]" /> : <ChevronRight className="h-4 w-4 text-[#888]" />}
            </button>
            {showHistory && (
              <div className="mt-3 space-y-1.5" data-testid="payout-history-list">
                {history.batches.length === 0 ? (
                  <div className="text-center text-[#666] text-xs py-3">Pa gen peman ki fèt ankò.</div>
                ) : history.batches.map((h, i) => (
                  <div key={h.batch + i} data-testid={`payout-batch-${i}`}
                    className="flex items-center justify-between gap-3 px-3 py-2 bg-[#0A0A0A] border border-[#1a1a1a] rounded-sm">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono-term text-xs text-white">{h.paid_at ? new Date(h.paid_at).toLocaleString() : h.batch}</span>
                        <span className={`text-[9px] font-mono-term uppercase px-1.5 py-0.5 rounded-sm ${h.method === "friday-auto" ? "bg-[#f5a623]/10 text-[#f5a623]" : "bg-[#1C8CFF]/10 text-[#1C8CFF]"}`}>
                          {h.method === "friday-auto" ? "Vandredi Auto" : "Manyèl"}
                        </span>
                      </div>
                      <div className="font-mono-term text-[10px] text-[#666]">{h.beneficiaries} benefisyè · {h.count} komisyon</div>
                    </div>
                    <div className="font-heading text-sm font-bold text-[#00F593] shrink-0">${h.total}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="border border-[#222] bg-[#0F0F0F]" data-testid="commission-ledger">
          {ledger.by_person.length === 0 ? <div className="p-8 text-center text-[#888] text-sm">Pa gen komisyon pou kounye a.</div> :
            ledger.by_person.map((b) => (
              <div key={b.beneficiary_id} className="px-4 py-3 border-b border-[#161616] last:border-0 flex items-center justify-between gap-3" data-testid={`commission-person-${b.beneficiary_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {(b.pending || 0) > 0 && (
                    <input type="checkbox" checked={selected.includes(b.beneficiary_id)}
                      onChange={() => toggleSelect(b.beneficiary_id)}
                      data-testid={`select-payout-${b.beneficiary_id}`}
                      className="h-4 w-4 accent-[#1C8CFF] shrink-0 cursor-pointer" />
                  )}
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-sm truncate">{b.name || b.email} <span className="text-[10px] font-mono-term uppercase text-[#1C8CFF] ml-1">{b.role}</span></div>
                    <div className="font-mono-term text-[11px] text-[#666]">Imèl pou peye: {b.payout_email || "— (poko mete)"} · {b.count} komisyon</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    {(b.pending || 0) > 0 ? (
                      <>
                        <div className="font-heading text-sm font-bold text-[#f5a623]">${b.pending || 0}</div>
                        <div className="font-mono-term text-[9px] uppercase text-[#555]">pending</div>
                      </>
                    ) : (
                      <>
                        <div className="font-heading text-sm font-bold text-[#00F593]">${b.paid || 0}</div>
                        <div className="font-mono-term text-[9px] uppercase text-[#555]">peye</div>
                      </>
                    )}
                  </div>
                  {(b.pending || 0) > 0 && (
                    <>
                      <Button size="sm" onClick={() => payAll(b.beneficiary_id)} disabled={busy === "payall-" + b.beneficiary_id} data-testid={`pay-all-${b.beneficiary_id}`}
                        className="h-8 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] text-xs font-semibold">Make Peye</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
